import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tabs, Tab, Box, Paper, Badge } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import HistoryIcon from "@mui/icons-material/History";
import DiscountIcon from "@mui/icons-material/Sell";
import LogoutIcon from "@mui/icons-material/Logout";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { AdminContext } from "./util/AdminContext";
import { postReq, getReq } from "../../configs/ApiUrl.js";
import AdminAddProduct from "./AdminAddProduct.js";
import AdminProductTable from "./AdminProductTable.js";
import LiveListings from "./LiveListings.js";
import ActiveOrders from "./ActiveOrders.js";
import ProductsHistory from "./ProductsHistory.js";
import DiscountCodes from "./DiscountCodes.js";
import PageHeader from "../PageHeader";
import PageFooter from "../PageFooter";

function AdminMenu() {
  const { t } = useTranslation(["admin", "altText", "userProfile"]);
  const navigateLogout = useNavigate();
  const { setIsAdmin } = useContext(AdminContext);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [liveListingCount, setLiveListingCount] = useState(0);
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [historyCount, setHistoryCount] = useState(0);
  const [userRole, setUserRole] = useState("admin");
  const [userEmail, setUserEmail] = useState("");
  const [subadminEmail, setSubadminEmail] = useState("");

  const handleLogOut = async () => {
    try {
      const logOutResp = await postReq("/user/logout");
      if (logOutResp?.success) {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        localStorage.removeItem("u");
        localStorage.removeItem("r");
        navigateLogout("/admin");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getLiveListingCount = async () => {
    const res = await getReq("/product/all");
    if (res?.allProducts) {
      const count = res.allProducts.filter((p) => {
        if (userRole === "admin")
          return ["approved", "preApproved"].includes(p.status);
        if (userRole === "subadmin")
          return (
            ["approved", "preApproved"].includes(p.status) &&
            p.subadminEmail === userEmail
          );
        return false;
      }).length;
      setLiveListingCount(count);
    } else {
      setLiveListingCount(0);
    }
  };

  const getActiveOrdersCount = async () => {
    const res = await getReq("/order/all");
    if (res?.success) {
      const count = res.allOrders.filter((o) => {
        return userRole === "admin"
          ? ["placed", "paid"].includes(o.orderStatus)
          : ["placed", "paid"].includes(o.orderStatus) &&
              o.subadminEmail === subadminEmail;
      }).length;
      setActiveOrdersCount(count);
    } else {
      setActiveOrdersCount(0);
    }
  };

  const getPendingApprovalCount = async () => {
    try {
      const res = await getReq("/product/all");
      if (res?.allProducts) {
        const count = res.allProducts.filter(
          (product) => product.status === "pending"
        ).length;
        setPendingApprovalCount(count);
      } else {
        setPendingApprovalCount(0);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/admin");
      }
    }
  };

  const getHistoryCount = async () => {
    const res = await getReq("/order/all");
    if (res?.success) {
      const count = res.allOrders.filter((o) => {
        const statuses = ["cancelled", "delivered"];
        return userRole === "admin"
          ? statuses.includes(o.orderStatus)
          : statuses.includes(o.orderStatus) &&
              o.subadminEmail === subadminEmail;
      }).length;
      setHistoryCount(count);
    } else {
      setHistoryCount(0);
    }
  };

  useEffect(() => {
    getLiveListingCount();
    getActiveOrdersCount();
    getPendingApprovalCount();
    getHistoryCount();
  }, [userRole, userEmail, subadminEmail]);

  const tabs = [
    {
      label: (
        <Badge
          badgeContent={pendingApprovalCount}
          color='primary'
          sx={{ mt: -1 }}>
          <span className='mt-1'>{t("admin:approvalButton")}</span>
        </Badge>
      ),
      icon: <ListAltIcon className='mr-1' />,
      component: <AdminProductTable />,
    },
    {
      label: (
        <Badge
          badgeContent={activeOrdersCount}
          color='primary'
          sx={{ mt: -1 }}>
          <span className='mt-1'>{t("admin:activeButton")}</span>
        </Badge>
      ),
      icon: <AssignmentIcon className='mr-1' />,
      component: <ActiveOrders />,
    },
    {
      label: (
        <Badge
          badgeContent={liveListingCount}
          color='primary'
          sx={{ mt: -1 }}>
          <span className='mt-1'>{t("admin:liveButton")}</span>
        </Badge>
      ),
      icon: <LocalShippingIcon className='mr-1' />,
      component: <LiveListings />,
    },
    {
      label: (
        <Badge
          badgeContent={historyCount}
          color='primary'
          sx={{ mt: -1 }}>
          <span className='mt-1'>{t("admin:historyButton")}</span>
        </Badge>
      ),
      icon: <HistoryIcon className='mr-1' />,
      component: <ProductsHistory />,
    },
    {
      label: t("admin:discountButton"),
      icon: <DiscountIcon className='mr-1' />,
      component: <DiscountCodes />,
    },
    {
      label: t("admin:addProductButton"),
      icon: <AddCircleOutlineIcon className='mr-1' />,
      component: <AdminAddProduct />,
    },
  ];

  return (
    <div className='flex flex-col min-h-screen'>
      <PageHeader
        onMenuClick={() => setSidebarOpen(!isSidebarOpen)}
        closeMenu={() => setSidebarOpen(false)}
      />

      <section className='flex flex-col flex-1 pt-20 px-1'>
        <Paper
          elevation={1}
          className='rounded-lg overflow-hidden'>
          <Tabs
            value={tabIndex}
            onChange={(e, newValue) => setTabIndex(newValue)}
            variant='scrollable'
            scrollButtons='auto'
            className='bg-white shadow-md border-b mt-4'
            TabIndicatorProps={{ style: { backgroundColor: "#6366F1" } }}>
            {tabs.map((tab, idx) => (
              <Tab
                key={idx}
                icon={tab.icon}
                iconPosition='start'
                label={
                  <span className='capitalize text-sm font-medium'>
                    {tab.label}
                  </span>
                }
                className='normal-case px-4'
              />
            ))}
          </Tabs>

          <Box className='p-4 bg-gray-50 min-h-[60vh]'>
            {tabs.map((tab, idx) => (
              <div
                key={idx}
                hidden={tabIndex !== idx}
                className='w-full'>
                {tabIndex === idx && tab.component}
              </div>
            ))}
          </Box>
        </Paper>
      </section>

      <button
        onClick={handleLogOut}
        className='fixed bottom-8 right-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full shadow-lg z-50'>
        <div className='flex items-center gap-2'>
          <LogoutIcon className='w-5 h-5' />
          <span>{t("userProfile:logoutButton")}</span>
        </div>
      </button>
    </div>
  );
}

export default AdminMenu;
